/**
 * Use in scenarios where you want an optionally controlled prop.
 * If the prop is defined its value will be used otherwise local state will be used.
 * Using the provided update function ensures that the state is updated and change callback function is called (if given).
 */

import { useState, useCallback } from 'react';
import { useLatest } from './useLatest.tsx';

type Callback<T> = (newValue: T) => void;

/**
 * Function definition without default value - return value is potentially undefined
 */
export function useControllable<T>(
	propValue: T | undefined,
	changeCallback: Callback<T> | undefined,
): [T | undefined, Callback<T>];

/**
 * Function definition with default value - return value will always be defined
 */
export function useControllable<T>(
	propValue: T | undefined,
	changeCallback: Callback<T> | undefined,
	defaultValue: T,
): [T, Callback<T>];

export function useControllable<T>(
	propValue: T | undefined,
	changeCallback: Callback<T> | undefined,
	defaultValue?: T,
): [T | undefined, Callback<T>] {
	const [stateValue, setStateValue] = useState<T | undefined>(defaultValue);
	const latestChangeCallback = useLatest(changeCallback);

	const value = propValue === undefined ? stateValue : propValue;

	const updateValue = useCallback(
		(newValue: T) => {
			setStateValue(newValue);
			latestChangeCallback.current?.(newValue);
		},
		[latestChangeCallback],
	);

	return [value, updateValue];
}
